import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { phoneEndpoint } from '../../services/api/endpoints/phone.endpoint';
import { useRegion } from './useRegion';
import { updateOrganisationField } from '../../store/slices/organizationSlice';
import { logger } from '../../utils/logger';

const MAX_TEST_CALLS = 3;

export const usePhoneActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  const { regionCode } = useRegion();

  const callAttempts = selectedOrganization?.twilioCallAttemptLimit ?? 0;
  const remainingCalls = Math.max(0, MAX_TEST_CALLS - callAttempts);
  const canMakeCall = remainingCalls > 0;

  const initiateTestCall = useCallback(
    async (phoneNumber: string) => {
      if (!selectedOrganization?.id) {
        throw new Error('No organization selected');
      }

      if (!regionCode) {
        throw new Error('Unable to determine region from timezone');
      }

      if (!canMakeCall) {
        throw new Error('Maximum test calls reached');
      }

      setIsLoading(true);
      setError(null);

      try {
        await phoneEndpoint.initiateTestCall(selectedOrganization.id, phoneNumber, regionCode);

        // Update only the call attempt counter in Redux
        dispatch(
          updateOrganisationField({
            field: 'twilioCallAttemptLimit',
            value: callAttempts + 1,
          })
        );

        logger.info('Test call initiated and counter updated', {
          orgId: selectedOrganization.id,
          country: regionCode,
          newCount: callAttempts + 1,
        });

        return { success: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to initiate call';
        setError(message);
        logger.error('Failed to initiate test call:', err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [selectedOrganization, regionCode, canMakeCall, callAttempts, dispatch]
  );

  return {
    initiateTestCall,
    isLoading,
    error,
    remainingCalls,
    canMakeCall,
    maxCalls: MAX_TEST_CALLS,
  };
};
